import React, { useEffect, useState } from 'react';
import { X, Phone, Save, Trash2 } from 'lucide-react';
import {
  clearMyAdminMobileNumber,
  getMyAdminMobileNumber,
  isValidMobilePhone,
  saveMyAdminMobileNumber,
} from '../../services/adminMobileNumbersService';
import { useToast } from '../../contexts/ToastContext';

interface SaveMobileNumberModalProps {
  onClose: () => void;
}

export function SaveMobileNumberModal({ onClose }: SaveMobileNumberModalProps) {
  const { showSuccess, showError } = useToast();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [hadExisting, setHadExisting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = await getMyAdminMobileNumber();
        if (cancelled) return;
        if (existing) {
          setPhone(existing);
          setHadExisting(true);
        }
      } catch (err) {
        console.error(err);
        showError('Could not load your mobile number.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidMobilePhone(phone)) {
      showError('Enter a valid mobile number (at least 7 digits). Prefer +353…');
      return;
    }
    setSaving(true);
    try {
      const row = await saveMyAdminMobileNumber(phone);
      setPhone(row.mobile_phone);
      setHadExisting(true);
      showSuccess('Mobile number saved for MFA setup.');
      onClose();
    } catch (err) {
      console.error(err);
      showError(err instanceof Error ? err.message : 'Failed to save mobile number.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!hadExisting) {
      setPhone('');
      return;
    }
    if (!window.confirm('Remove your saved mobile number?')) return;
    setClearing(true);
    try {
      await clearMyAdminMobileNumber();
      setPhone('');
      setHadExisting(false);
      showSuccess('Mobile number removed.');
    } catch (err) {
      console.error(err);
      showError(err instanceof Error ? err.message : 'Failed to remove mobile number.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-white/20 w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Mobile number</h2>
              <p className="text-sm text-slate-600">For Supabase Phone MFA</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              Save the mobile number that should receive MFA codes. Use international format when
              possible (e.g. <span className="font-mono text-slate-800">+353871234567</span>).
            </p>
            <div>
              <label htmlFor="admin-mobile-phone" className="block text-sm font-medium text-slate-700 mb-2">
                Mobile number
              </label>
              <input
                id="admin-mobile-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+353 87 123 4567"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={handleClear}
                disabled={clearing || (!hadExisting && !phone)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl disabled:opacity-40"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {clearing ? 'Removing…' : 'Clear'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium rounded-xl shadow-md hover:shadow-lg disabled:opacity-60"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save number'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
